(() => {
  'use strict';

  const PAGE_SELECTOR = '[data-atemp-wishlist-page]';
  const STORAGE_KEY = 'atemp_wishlist_v1';
  const CHANGE_EVENT = 'atemp:wishlist:change';
  const PRODUCT_CACHE = new Map();

  const page = document.querySelector(PAGE_SELECTOR);
  if (!page || page.dataset.atempWishlistPageReady === 'true') return;

  page.dataset.atempWishlistPageReady = 'true';

  const loading = page.querySelector('[data-atemp-wishlist-loading]');
  const empty = page.querySelector('[data-atemp-wishlist-empty]');
  const grid = page.querySelector('[data-atemp-wishlist-grid]');
  const summary = page.querySelector('[data-atemp-wishlist-summary]');

  const locale = page.dataset.locale || document.documentElement.lang || 'pt-BR';
  const currency =
    page.dataset.currency ||
    window.Shopify?.currency?.active ||
    'BRL';

  const productRoot =
    window.Shopify?.routes?.root ||
    page.dataset.productRoot ||
    '/';

  let renderToken = 0;

  const readStorageFallback = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);
      const items = Array.isArray(parsed) ? parsed : parsed?.items;

      return Array.isArray(items) ? items : [];
    } catch (error) {
      console.warn('[ATEMP Wishlist Page] Não foi possível ler os favoritos.', error);
      return [];
    }
  };

  const getItems = () => {
    if (window.ATEMPWishlist?.getItems) {
      return window.ATEMPWishlist.getItems();
    }

    return readStorageFallback();
  };

  const formatMoney = (value) => {
    const amount = Number(value);

    if (!Number.isFinite(amount)) return '';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(amount / 100);
    } catch {
      return `R$ ${(amount / 100).toFixed(2).replace('.', ',')}`;
    }
  };

  const normalizeImage = (image, fallback = '') => {
    if (!image) return fallback;

    if (typeof image === 'string') return image;

    return image.src || image.url || fallback;
  };

  const productUrl = (product, item) => {
    if (product?.url) return product.url;
    if (item?.url) return item.url;

    return `${productRoot}products/${encodeURIComponent(
      product?.handle || item?.handle || ''
    )}`;
  };

  const fetchProduct = async (item) => {
    const handle = String(item?.handle || '');
    if (!handle) return null;

    if (!PRODUCT_CACHE.has(handle)) {
      const root = productRoot.endsWith('/') ? productRoot : `${productRoot}/`;
      const url = `${root}products/${encodeURIComponent(handle)}.js`;

      PRODUCT_CACHE.set(
        handle,
        fetch(url, {
          headers: {
            Accept: 'application/json',
          },
          credentials: 'same-origin',
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Produto indisponível: ${response.status}`);
            }

            return response.json();
          })
          .catch((error) => {
            console.warn(
              `[ATEMP Wishlist Page] Não foi possível atualizar ${handle}.`,
              error
            );
            return null;
          })
      );
    }

    return PRODUCT_CACHE.get(handle);
  };

  const createHeartButton = (product, item) => {
    const button = document.createElement('button');
    const id = String(product?.id || item.id || '');
    const handle = String(product?.handle || item.handle || '');
    const title = String(product?.title || item.title || 'Produto');
    const url = productUrl(product, item);
    const image = normalizeImage(
      product?.featured_image,
      normalizeImage(product?.images?.[0], item.image || '')
    );

    button.type = 'button';
    button.className =
      'atemp-wishlist-button atemp-wishlist-button--card atemp-wishlist-page__remove';
    button.dataset.atempWishlistButton = '';
    button.dataset.productId = id;
    button.dataset.productHandle = handle;
    button.dataset.productTitle = title;
    button.dataset.productUrl = url;
    button.dataset.productImage = image;
    button.setAttribute('aria-pressed', 'true');
    button.setAttribute('aria-label', `Remover ${title} dos favoritos`);
    button.title = 'Remover dos favoritos';

    button.innerHTML = `
      <span class="atemp-wishlist-button__icon" aria-hidden="true">
        <svg
          class="atemp-wishlist-button__svg atemp-wishlist-button__svg--outline"
          viewBox="0 0 24 24"
          focusable="false"
        >
          <path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.76 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.26 22 8.5 22 5.42 19.58 3 16.5 3Zm-4.4 15.55-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05Z"/>
        </svg>
        <svg
          class="atemp-wishlist-button__svg atemp-wishlist-button__svg--filled"
          viewBox="0 0 24 24"
          focusable="false"
        >
          <path d="m12 21.35-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35Z"/>
        </svg>
      </span>
    `;

    return button;
  };

  const createCard = (product, item) => {
    const card = document.createElement('article');
    card.className = 'atemp-wishlist-card';

    const media = document.createElement('div');
    media.className = 'atemp-wishlist-card__media';

    const link = document.createElement('a');
    link.className = 'atemp-wishlist-card__image-link';
    link.href = productUrl(product, item);

    const title = String(product?.title || item.title || 'Produto');
    link.setAttribute('aria-label', title);

    const imageUrl = normalizeImage(
      product?.featured_image,
      normalizeImage(product?.images?.[0], item.image || '')
    );

    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'atemp-wishlist-card__image';
      image.src = imageUrl;
      image.alt = title;
      image.loading = 'lazy';
      image.decoding = 'async';
      link.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'atemp-wishlist-card__placeholder';
      placeholder.textContent = 'Imagem indisponível';
      link.appendChild(placeholder);
    }

    media.appendChild(link);
    media.appendChild(createHeartButton(product, item));

    const content = document.createElement('div');
    content.className = 'atemp-wishlist-card__content';

    const titleLink = document.createElement('a');
    titleLink.className = 'atemp-wishlist-card__title';
    titleLink.href = productUrl(product, item);
    titleLink.textContent = title;
    content.appendChild(titleLink);

    if (product) {
      const priceRow = document.createElement('div');
      priceRow.className = 'atemp-wishlist-card__price-row';

      if (product.price_varies) {
        const prefix = document.createElement('span');
        prefix.className = 'atemp-wishlist-card__price-prefix';
        prefix.textContent = 'A partir de';
        priceRow.appendChild(prefix);
      }

      const price = document.createElement('span');
      price.className = 'atemp-wishlist-card__price';
      price.textContent = formatMoney(product.price);
      priceRow.appendChild(price);

      const compareAt = Number(product.compare_at_price);
      const currentPrice = Number(product.price);

      if (
        Number.isFinite(compareAt) &&
        Number.isFinite(currentPrice) &&
        compareAt > currentPrice
      ) {
        const compare = document.createElement('s');
        compare.className = 'atemp-wishlist-card__compare-price';
        compare.textContent = formatMoney(compareAt);
        priceRow.appendChild(compare);
      }

      content.appendChild(priceRow);

      if (!product.available) {
        const status = document.createElement('span');
        status.className =
          'atemp-wishlist-card__status atemp-wishlist-card__status--unavailable';
        status.textContent = 'Esgotado';
        content.appendChild(status);
      }
    } else {
      const status = document.createElement('span');
      status.className =
        'atemp-wishlist-card__status atemp-wishlist-card__status--unavailable';
      status.textContent = 'Dados indisponíveis';
      content.appendChild(status);
    }

    card.append(media, content);
    return card;
  };

  const setView = (view) => {
    loading.hidden = view !== 'loading';
    empty.hidden = view !== 'empty';
    grid.hidden = view !== 'grid';
  };

  const updateSummary = (count) => {
    if (!summary) return;

    summary.hidden = count === 0;
    summary.textContent =
      count === 1
        ? '1 produto salvo'
        : `${count} produtos salvos`;
  };

  const render = async () => {
    const token = ++renderToken;
    const items = [...getItems()].sort(
      (a, b) => Number(b.addedAt || 0) - Number(a.addedAt || 0)
    );

    updateSummary(items.length);

    if (items.length === 0) {
      grid.replaceChildren();
      setView('empty');
      return;
    }

    setView('loading');

    const products = await Promise.all(
      items.map((item) => fetchProduct(item))
    );

    if (token !== renderToken) return;

    const fragment = document.createDocumentFragment();

    items.forEach((item, index) => {
      fragment.appendChild(createCard(products[index], item));
    });

    grid.replaceChildren(fragment);
    setView('grid');

    window.ATEMPWishlist?.sync?.(grid);
  };

  const handleChange = () => {
    render();
  };

  const handleStorage = (event) => {
    if (event.key === STORAGE_KEY) render();
  };

  window.addEventListener(CHANGE_EVENT, handleChange);
  window.addEventListener('storage', handleStorage);
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.(PAGE_SELECTOR)) {
      render();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render, { once: true });
  } else {
    render();
  }
})();